import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useHaptics } from "@/hooks/useHaptics";

export interface SavedAirport {
  id: string;
  airport_code: string;
  created_at: string;
}

export const useSavedAirports = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { notification, impact } = useHaptics();
  const [savedAirports, setSavedAirports] = useState<SavedAirport[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSaved = useCallback(async () => {
    if (!user) {
      setSavedAirports([]);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("saved_airports")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (!error && data) setSavedAirports(data);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchSaved();
  }, [fetchSaved]);

  const isSaved = useCallback(
    (code: string) => savedAirports.some((a) => a.airport_code === code),
    [savedAirports]
  );

  const toggleSave = useCallback(
    async (code: string) => {
      if (!user) return;

      if (isSaved(code)) {
        const { error } = await supabase
          .from("saved_airports")
          .delete()
          .eq("user_id", user.id)
          .eq("airport_code", code);

        if (!error) {
          setSavedAirports((prev) => prev.filter((a) => a.airport_code !== code));
          impact("light");
          toast({ title: "Airport removed", description: `${code} removed from your saved airports.` });
        }
      } else {
        const { data, error } = await supabase
          .from("saved_airports")
          .insert({ user_id: user.id, airport_code: code })
          .select()
          .single();

        if (!error && data) {
          setSavedAirports((prev) => [data, ...prev]);
          notification("success");
          toast({ title: "Airport saved!", description: `${code} added to your saved airports.` });
        }
      }
    },
    [user, isSaved, toast, impact, notification]
  );

  return { savedAirports, loading, isSaved, toggleSave };
};
